/**
 * @file GameTicker.ts
 * @role The single frame pump. Wraps the Pixi ticker and is the ONLY place raw frame
 *       delta is read. Clamps it (background tabs throttle rAF; an unclamped delta makes
 *       reels teleport) and dispatches a clamped dt to every subscribed system.
 * @owns The clamp, the subscriber set, and tab-focus pause.
 * @reads The Pixi ticker's deltaMS (only here).
 * @neverDoes Run game logic. Read wall-clock time for physics. Allow unclamped dt out.
 * @invariants 22 (all reel/particle/effect physics advance on clamped GameTicker dt).
 * @see docs/INVARIANTS.md, docs/decisions/0003-deterministic-reel-engine.md
 */
import type { Ticker } from 'pixi.js';

export type TickListener = (deltaMs: number) => void;

export class GameTicker {
  private readonly listeners = new Set<TickListener>();
  private pixiTicker: Ticker | undefined;
  private readonly pixiCallback = (): void => {
    if (this.pixiTicker) this.advance(this.pixiTicker.deltaMS);
  };
  private paused = false;

  /** @param maxDeltaMs upper clamp; raw deltas above this are capped (default 50ms). */
  constructor(private readonly maxDeltaMs = 50) {}

  /** Bind to the Pixi ticker so each frame drives advance(). */
  attach(ticker: Ticker): void {
    this.detach();
    this.pixiTicker = ticker;
    ticker.add(this.pixiCallback);
  }

  detach(): void {
    if (this.pixiTicker) {
      this.pixiTicker.remove(this.pixiCallback);
      this.pixiTicker = undefined;
    }
  }

  /** Subscribe to clamped per-frame dt. Returns an unsubscribe function. */
  add(listener: TickListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Advance one frame with a RAW delta (ms). Clamps, then dispatches the clamped dt.
   * Exposed (not just driven by Pixi) so tests and focus-recovery can step deterministically.
   */
  advance(rawDeltaMs: number): void {
    if (this.paused) return;
    const dt = this.clamp(rawDeltaMs);
    for (const listener of [...this.listeners]) {
      listener(dt);
    }
  }

  /** The clamp itself: never negative, never above maxDeltaMs. */
  clamp(rawDeltaMs: number): number {
    if (!Number.isFinite(rawDeltaMs) || rawDeltaMs < 0) return 0;
    return Math.min(rawDeltaMs, this.maxDeltaMs);
  }

  /** Stop advancing physics (e.g. while the tab is hidden). Network is unaffected. */
  pause(): void {
    this.paused = true;
  }

  resume(): void {
    this.paused = false;
  }

  get isPaused(): boolean {
    return this.paused;
  }

  get maxDelta(): number {
    return this.maxDeltaMs;
  }
}
